// src/pages/employer/EmployerDashboard.jsx

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { jobAdAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { LayoutList, LayoutGrid } from 'lucide-react';

const scoreBand = (s) => s >= 80 ? 'score-high' : s >= 60 ? 'score-mid' : s >= 40 ? 'score-low' : 'score-poor';

const StatusTag = ({ status }) => {
  const map = { ACTIVE: 'tag-green', DRAFT: 'tag-ghost', CLOSED: 'tag-red', SHORTLISTED: 'tag-green', REJECTED: 'tag-red', REVIEWED: '' };
  return <span className={`tag ${map[status] || ''}`}>{status}</span>;
};

const ViewToggle = ({ view, setView }) => (
  <div className="view-toggle">
    <button className={`view-toggle-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')}>
      <LayoutList size={13} /> List
    </button>
    <button className={`view-toggle-btn ${view === 'card' ? 'active' : ''}`} onClick={() => setView('card')}>
      <LayoutGrid size={13} /> Cards
    </button>
  </div>
);

export const EmployerDashboard = () => {
  const { user, activeOrg, activeProfile } = useAuth();
  const [ads, setAds]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobView, setJobView] = useState('list');

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    jobAdAPI.getJobAds(activeOrg.id, activeProfile.id)
      .then(({ data }) => setAds(data.data.ads || []))
      .finally(() => setLoading(false));
  }, [activeOrg, activeProfile]);

  const activeJobs = ads.filter(a => a.status === 'ACTIVE').length;
  const draftJobs  = ads.filter(a => a.status === 'DRAFT').length;
  const jobs       = ads; // alias for remaining display code

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Employer Dashboard</h1>
        <p>Welcome back, {user?.email}</p>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-box"><div className="stat-box-value">{activeJobs}</div><div className="stat-box-label">Active Jobs</div></div>
        <div className="stat-box"><div className="stat-box-value">{draftJobs}</div><div className="stat-box-label">Draft Jobs</div></div>
        <div className="stat-box"><div className="stat-box-value">{ads.reduce((s, a) => s + (a.positions?.length || 0), 0)}</div><div className="stat-box-label">Positions</div></div>
        <div className="stat-box"><div className="stat-box-value">{ads.length}</div><div className="stat-box-label">All Ads</div></div>
      </div>

      <div className="form-row">

        {/* Jobs */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)' }}>Recent Jobs</span>
              <Link to="/employer/job-ads" style={{ fontSize: '11px', color: 'var(--secondary)' }}>View all →</Link>
            </div>
            <ViewToggle view={jobView} setView={setJobView} />
          </div>

          {loading ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : jobs.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '10px' }}>No job ads yet.</p>
              <Link to="/employer/job-ads" className="btn btn-primary btn-sm">Create your first job ad →</Link>
            </div>
          ) : jobView === 'list' ? (
            <div className="job-list">
              {jobs.map(job => (
                <Link key={job.id} to="/employer/job-ads" className="job-row" style={{ textDecoration: 'none' }}>
                  <div className="job-row-logo">{job.title?.[0] || 'J'}</div>
                  <div className="job-row-main">
                    <div className="job-row-title">{job.title}</div>
                    <div className="job-row-meta">
                      <StatusTag status={job.status} />
                      <span className="tag tag-ghost">{job.positions?.length || 0} position{job.positions?.length !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="job-card-grid">
              {jobs.map(job => (
                <Link key={job.id} to="/employer/job-ads" className="job-card" style={{ textDecoration: 'none' }}>
                  <div className="job-card-logo">{job.title?.[0] || 'J'}</div>
                  <div className="job-card-title">{job.title}</div>
                  <div className="job-card-meta">
                    <StatusTag status={job.status} />
                    <span className="tag tag-ghost">{job.positions?.length || 0} positions</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Positions */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)' }}>Active Positions</span>
          </div>
          <div className="job-list">
            {loading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Loading...</div>
            ) : ads.filter(a => a.status === 'ACTIVE').flatMap(a => (a.positions || []).map(p => ({ ...p, adTitle: a.title }))).length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                No active positions. Publish a job ad to get started.
              </div>
            ) : ads.filter(a => a.status === 'ACTIVE').flatMap(a => (a.positions || []).map(p => ({ ...p, adTitle: a.title }))).slice(0, 6).map(pos => (
              <div key={pos.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '1px solid var(--border-lt)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                    {pos.jobDefinition?.name || '—'}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{pos.adTitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
